import {
  infiniteQueryOptions,
  queryOptions,
  type InfiniteData,
  type Query,
  type QueryClient,
} from "@tanstack/react-query";
import { isPermissionError } from "@/lib/is-permission-error";
import type { IssueArchiveService } from "@/services/issue-archive.service";
import type { WorkItemService } from "@/services/work-item.service";
import type { TIssue, TIssueListResponse, TIssuePagination, TIssueQuery } from "@/types/issue";
import { peekQueryClient } from "./client";
import { rereadAfterWrite } from "./invalidation";
import { myWorkChanged } from "./my-work";
import { FRESHNESS, type ReadRequest } from "./policies";
import { scopedKey, scopedRead, type QueryScope } from "./scope";

/**
 * The work-item list reads (NG-2806).
 * Every key is `[slug, uid, 'work-items', family, …]`, and a family's entries are
 * keyed by the request's own parameters, never by the screen asking: two
 * screens that ask the same thing share an entry, and two that ask different
 * things (the Gantt's `expand`, the overview milestones' unfiltered read) no
 * longer overwrite one shared slot, which is what the store's `#2638` load
 * tokens and the NG-900 Gantt race were about.
 *
 * - **project** `[…, 'project', projectId, params]`: a project's work items,
 *   cursor-paged when `params.pagination` is `"cursor"` (the Table), one page
 *   otherwise (Board, Calendar, Gantt, the overview milestones, the tab count).
 * - **archived** `[…, 'archived', projectId, params]`: the project's archive.
 * - **workspace** `[…, 'workspace', params]`: the workspace list, always
 *   cursor-paged whether or not `params` names a `pagination`
 *   (the Tasks and Events tables, saved workspace views, My Space tabs).
 * - **workspace-full** `[…, 'workspace-full', params, cap]`: the complete
 *   filtered workspace set, up to `cap` rows, for the Board, Calendar and Gantt
 *   that lay out every row rather than a page.
 * - **workspace-total** `[…, 'workspace-total', params]`: the filters' total
 *   without rows, for the header count of a windowed layout (NG-1182).
 * - **sub-issues** `[…, 'sub-issues', parentId]`: one work item's active
 *   children (`queries/work-item-sub-issues.ts`), cached as a full set. A
 *   family here rather than its own domain, so every writer below reaches a
 *   sub-task's row as it reaches any list's, and a list re-read re-reads them.
 *
 * All are mutable working data (the full set without the minute refresh, see
 * below), and plain GETs, so all declare `"safe-read"`.
 *
 * One work item's full record (the task page, the side panel) is its own
 * domain, `[slug, uid, 'work-item', idOrIdentifier]` (`workItemDetailQuery`):
 * singular, so no list predicate matches it, and separate from the lists so a
 * list re-read can never put a lean row (no description) over it (NG-2889).
 * Pages are cached normalized (`WorkItemsPage`): a grouped response is
 * flattened as the store did, so every writer patches one shape.
 *
 * @writers `IssueStore`'s row writers (field, date, skill and predecessor edits)
 * patch the row by id in every cached list through `writeWorkItemRows`, putting
 * back only their own rows on failure. Create, delete, archive, restore and the
 * bulk actions drop rows they know have left and then re-read the lists, since
 * which rows a filtered list holds is the server's answer. The writers outside
 * the store (job-role smart assign and delete, the custom-field cell, the quick
 * view's event time) patch through the same helpers.
 *
 * The same helpers reach a cached full record of a written work item: a patch
 * rewrites it, a failed write puts it back, and a delete drops it. A write
 * outside the store (`workItemListsChanged`) marks the records stale too and
 * re-reads the open ones.
 *
 * My Work's feed holds work items under its own key (`queries/my-work.ts`), so
 * no list predicate here reaches it; `writeWorkItemRows`,
 * `invalidateWorkItemLists` and `workItemListsChanged` re-read it as well
 * (`myWorkChanged`, NG-3586). Its rows are a different shape, so they are never
 * patched.
 */

/** One normalized page of a work-item list. */
export interface WorkItemsPage {
  readonly rows: TIssue[];
  readonly pagination: TIssuePagination;
}

/** The complete filtered workspace set, up to its cap. */
export interface WorkItemsFullSet {
  readonly rows: TIssue[];
  /** The response's total, which on a windowed layout is the window's. */
  readonly totalCount: number | null;
  /** More rows match than the cap allowed. */
  readonly truncated: boolean;
}

/** A list's parameters: everything but the cursor, which the page walk owns. */
export type WorkItemsParams = Omit<TIssueQuery, "cursor">;

/** The lists' domain; `queries/work-item-sub-issues.ts` keys its family under it too. */
export const WORK_ITEMS_DOMAIN = "work-items";
const DOMAIN = WORK_ITEMS_DOMAIN;
const DETAIL_DOMAIN = "work-item";

type Family =
  | "project"
  | "archived"
  | "workspace"
  | "workspace-full"
  | "workspace-total"
  | "sub-issues";

/** A project's list key. */
export function projectWorkItemsKey(scope: QueryScope, projectId: string, params: WorkItemsParams) {
  return scopedKey(scope, DOMAIN, "project", projectId, params);
}

/** A project's archive key. */
export function archivedWorkItemsKey(
  scope: QueryScope,
  projectId: string,
  params: WorkItemsParams,
) {
  return scopedKey(scope, DOMAIN, "archived", projectId, params);
}

/** The workspace list key. */
export function workspaceWorkItemsKey(scope: QueryScope, params: WorkItemsParams) {
  return scopedKey(scope, DOMAIN, "workspace", params);
}

/** The workspace full-set key. */
export function workspaceFullSetKey(scope: QueryScope, params: WorkItemsParams, cap: number) {
  return scopedKey(scope, DOMAIN, "workspace-full", params, cap);
}

/** The workspace filter-total key. */
export function workspaceTotalKey(scope: QueryScope, params: WorkItemsParams) {
  return scopedKey(scope, DOMAIN, "workspace-total", params);
}

/** Flatten a (possibly grouped) list response into one page. */
export function toWorkItemsPage(response: TIssueListResponse): WorkItemsPage {
  const pagination: TIssuePagination = {
    next_cursor: response.next_cursor,
    prev_cursor: response.prev_cursor,
    next_page_results: response.next_page_results,
    prev_page_results: response.prev_page_results,
    total_count: response.total_count,
    total_count_all_depths: response.total_count_all_depths ?? null,
    count: response.count,
    total_pages: response.total_pages,
    total_results: response.total_results,
  };
  if (Array.isArray(response.results)) return { rows: response.results, pagination };
  const rows: TIssue[] = [];
  for (const value of Object.values(response.results ?? {})) {
    rows.push(...(Array.isArray(value) ? value : (value.results ?? [])));
  }
  return { rows, pagination };
}

function nextCursor(last: WorkItemsPage, cursor: string | null): string | undefined {
  const { next_cursor: next, next_page_results: more } = last.pagination;
  return more && next && next !== cursor ? next : undefined;
}

/**
 * A list's background re-reads, held while the server refuses it. A refusal
 * lasts until the member's role changes, and re-reading a list with no rows puts
 * it back to loading, so the page flashed between "Access restricted" and its
 * loading rows every minute and on every focus (NG-2101). A mount reads again.
 */
function unlessRefused<T>(policy: T) {
  return (query: Pick<Query, "state">) => (isPermissionError(query.state.error) ? false : policy);
}

const MUTABLE_LIST_REREADS = {
  refetchInterval: unlessRefused(FRESHNESS.mutable.refetchInterval),
  refetchOnWindowFocus: unlessRefused(FRESHNESS.mutable.refetchOnWindowFocus),
  refetchOnReconnect: unlessRefused(FRESHNESS.mutable.refetchOnReconnect),
};

function pagedWorkItems(
  queryKey: readonly unknown[],
  params: WorkItemsParams,
  read: (query: TIssueQuery, request: ReadRequest) => Promise<TIssueListResponse>,
  // Only a cursor request has further pages; a project's one-shot read (the
  // Board, the Calendar's window) is complete in its first answer whatever it says.
  paged = params.pagination === "cursor",
) {
  return infiniteQueryOptions({
    ...FRESHNESS.mutable,
    ...MUTABLE_LIST_REREADS,
    queryKey,
    initialPageParam: null as string | null,
    queryFn: scopedRead<WorkItemsPage, string | null>(
      async (request, { pageParam }) =>
        toWorkItemsPage(await read(pageParam ? { ...params, cursor: pageParam } : params, request)),
      { retry: "safe-read" },
    ),
    getNextPageParam: (last: WorkItemsPage, _all, cursor) =>
      paged ? nextCursor(last, cursor) : undefined,
  });
}

/** A project's work items. */
export function projectWorkItemsQuery(
  service: Pick<WorkItemService, "listWorkItems">,
  scope: QueryScope,
  projectId: string,
  params: WorkItemsParams,
) {
  return pagedWorkItems(projectWorkItemsKey(scope, projectId, params), params, (query, request) =>
    service.listWorkItems(scope.tenant, projectId, query, request),
  );
}

/** A project's archived work items. */
export function archivedWorkItemsQuery(
  service: Pick<IssueArchiveService, "listArchivedIssues">,
  scope: QueryScope,
  projectId: string,
  params: WorkItemsParams,
) {
  return pagedWorkItems(archivedWorkItemsKey(scope, projectId, params), params, (query, request) =>
    service.listArchivedIssues(scope.tenant, projectId, query, request),
  );
}

/** The workspace's work items. */
export function workspaceWorkItemsQuery(
  service: Pick<WorkItemService, "listWorkspaceWorkItems">,
  scope: QueryScope,
  params: WorkItemsParams,
) {
  // Always paged: the Tasks and Events tables send no `pagination` (the endpoint
  // pages by cursor by default) and load the rest on scroll, as the store
  // followed `next_cursor`. Read as one-shot, the list stopped at its first page
  // and a group's count lost its "N+" mark.
  return pagedWorkItems(
    workspaceWorkItemsKey(scope, params),
    params,
    (query, request) => service.listWorkspaceWorkItems(scope.tenant, query, request),
    true,
  );
}

/** The workspace list's page size for the full-set walk. */
const FULL_SET_PAGE_SIZE = 100;

/**
 * The complete filtered workspace set, walking cursor pages up to `cap` rows.
 * A larger set stops at the cap and says `truncated`, so the layout can say
 * "showing the first N" rather than silently dropping rows.
 */
export function workspaceFullSetQuery(
  service: Pick<WorkItemService, "listWorkspaceWorkItems">,
  scope: QueryScope,
  params: WorkItemsParams,
  cap: number,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    ...MUTABLE_LIST_REREADS,
    // No interval refresh: each one walks up to `cap / 100` pages, per open
    // Board or Gantt, every minute. The store never re-polled it; writes patch
    // it, and a mount or focus after 30 s re-reads it.
    refetchInterval: false,
    queryKey: workspaceFullSetKey(scope, params, cap),
    queryFn: scopedRead<WorkItemsFullSet>(
      async (request) => {
        const rows: TIssue[] = [];
        let cursor: string | null = null;
        let totalCount: number | null = null;
        let truncated = false;
        // A hard stop against a cursor that never ends, independent of the cap.
        const maxPages = Math.ceil(cap / FULL_SET_PAGE_SIZE) + 1;
        for (let page = 0; page < maxPages; page += 1) {
          const response: TIssueListResponse = await service.listWorkspaceWorkItems(
            scope.tenant,
            {
              ...params,
              pagination: "cursor",
              per_page: FULL_SET_PAGE_SIZE,
              ...(cursor ? { cursor } : {}),
            },
            request,
          );
          rows.push(...toWorkItemsPage(response).rows);
          totalCount = response.total_count ?? totalCount;
          if (rows.length >= cap) {
            truncated = response.next_page_results;
            break;
          }
          if (!response.next_page_results || !response.next_cursor) break;
          cursor = response.next_cursor;
        }
        const capped = rows.slice(0, cap);
        truncated = truncated || (totalCount !== null && totalCount > capped.length);
        return { rows: capped, totalCount, truncated };
      },
      { retry: "safe-read" },
    ),
  });
}

/**
 * The total matching `params`, without its rows. `per_page: 1` because only the
 * count is read; the caller strips the window and `expand` (NG-1182).
 */
export function workspaceTotalQuery(
  service: Pick<WorkItemService, "listWorkspaceWorkItems">,
  scope: QueryScope,
  params: WorkItemsParams,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: workspaceTotalKey(scope, params),
    queryFn: scopedRead<number | null>(
      async (request) =>
        (await service.listWorkspaceWorkItems(scope.tenant, { ...params, per_page: 1 }, request))
          .total_count ?? null,
      { retry: "safe-read" },
    ),
  });
}

/**
 * One work item's full record key, by whatever the screen holds: its id (the
 * side panel) or its identifier, `ALPHA-12` (the task page's URL). The writers
 * find a record by its `id` field, so one under an identifier key is patched
 * like any other.
 */
export function workItemDetailKey(scope: QueryScope, idOrIdentifier: string) {
  return scopedKey(scope, DETAIL_DOMAIN, idOrIdentifier);
}

/**
 * A work item's FULL record, at workspace scope (which resolves an id or an
 * identifier, and a project-less item). Never seeded from a list row: a list
 * serializes the lean shape, with no description, and an editor seeded from it
 * autosaved an empty description over the saved one (NG-2422).
 *
 * Access-sensitive: the record carries the viewer's `permissions` and what they
 * may see of it. Reused for 60 s, then re-read on mount, focus or reconnect;
 * the `access:self` hint re-reads it at once.
 */
export function workItemDetailQuery(
  service: Pick<WorkItemService, "getWorkItemByIdentifier">,
  scope: QueryScope,
  idOrIdentifier: string,
) {
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    queryKey: workItemDetailKey(scope, idOrIdentifier),
    queryFn: scopedRead<TIssue>(
      (request) => service.getWorkItemByIdentifier(scope.tenant, idOrIdentifier, request),
      { retry: "safe-read" },
    ),
  });
}

/** The rows of every loaded page, first page first. */
export function workItemPageRows(data: InfiniteData<WorkItemsPage> | undefined): TIssue[] {
  return data?.pages.flatMap((page) => page.rows) ?? [];
}

// ---- Writers ------------------------------------------------------------

type Pages = InfiniteData<WorkItemsPage, string | null>;
type ListData = Pages | WorkItemsFullSet;

function familyOf(query: Query): Family | undefined {
  return query.queryKey[2] === DOMAIN ? (query.queryKey[3] as Family) : undefined;
}

/** An entry of `tenant` that holds rows (every family but the total), under any principal. */
function isRowListQuery(tenant: string, query: Query): boolean {
  const family = familyOf(query);
  return query.queryKey[0] === tenant && family !== undefined && family !== "workspace-total";
}

/** Any work-item list entry of `tenant`, the totals included. */
function isAnyListQuery(tenant: string, query: Query): boolean {
  return query.queryKey[0] === tenant && familyOf(query) !== undefined;
}

function isPages(data: ListData): data is Pages {
  return "pages" in data;
}

function rowsOf(data: ListData): TIssue[] {
  return isPages(data) ? data.pages.flatMap((page) => page.rows) : data.rows;
}

/** Rewrite each row; `null` drops it. Counts follow the drops. */
function mapRows(data: ListData, map: (row: TIssue) => TIssue | null): ListData {
  if (isPages(data)) {
    let changed = false;
    const pages = data.pages.map((page) => {
      const rows: TIssue[] = [];
      for (const row of page.rows) {
        const next = map(row);
        if (next !== row) changed = true;
        if (next) rows.push(next);
      }
      const dropped = page.rows.length - rows.length;
      if (dropped === 0)
        return rows.length === page.rows.length && !changed ? page : { ...page, rows };
      const total = page.pagination.total_count;
      return {
        rows,
        pagination: {
          ...page.pagination,
          total_count: total === null ? null : Math.max(0, total - dropped),
        },
      };
    });
    return changed ? { ...data, pages } : data;
  }
  let changed = false;
  const rows: TIssue[] = [];
  for (const row of data.rows) {
    const next = map(row);
    if (next !== row) changed = true;
    if (next) rows.push(next);
  }
  if (!changed) return data;
  const dropped = data.rows.length - rows.length;
  const total = data.totalCount;
  return {
    ...data,
    rows,
    totalCount: total === null ? null : Math.max(0, total - dropped),
  };
}

/**
 * Rewrite cached data without changing when it was read: a write's patch is not
 * a read, and `writeWorkItemRows` tells entries read during the write apart by
 * their `dataUpdatedAt`.
 */
function rewrite(client: QueryClient, query: Query, update: (current: ListData) => ListData) {
  const current = query.state.data as ListData | undefined;
  if (current === undefined) return;
  const next = update(current);
  if (next === current) return;
  client.setQueryData<ListData>(query.queryKey, next, { updatedAt: query.state.dataUpdatedAt });
}

function rowLists(client: QueryClient, tenant: string): Query[] {
  return client.getQueryCache().findAll({ predicate: (query) => isRowListQuery(tenant, query) });
}

/** A cached full record of `tenant`, under any principal. */
function isDetailQuery(tenant: string, query: Query): boolean {
  return query.queryKey[0] === tenant && query.queryKey[2] === DETAIL_DOMAIN;
}

/**
 * The full records of these work items: by the record's own id, or, for one
 * whose first read has not landed, by the id its key names.
 */
export function isDetailOf(tenant: string, ids: ReadonlySet<string>) {
  return (query: Query): boolean => {
    if (!isDetailQuery(tenant, query)) return false;
    const record = query.state.data as TIssue | undefined;
    return record ? ids.has(record.id) : ids.has(String(query.queryKey[3]));
  };
}

function detailRecords(client: QueryClient, tenant: string): Query[] {
  return client.getQueryCache().findAll({ predicate: (query) => isDetailQuery(tenant, query) });
}

/** Replace a full record without changing when it was read (see `rewrite`). */
function rewriteRecord(client: QueryClient, query: Query, next: TIssue): void {
  if (query.state.data === next) return;
  client.setQueryData<TIssue>(query.queryKey, next, { updatedAt: query.state.dataUpdatedAt });
}

/**
 * A work item as the cache holds it: its full record if one has been read,
 * else as some cached list holds it. A recurring series' occurrence rows share
 * its id, so the series' own row is preferred, as `getIssueById` did.
 *
 * A record a write has marked stale yields to a list row read after it. A bulk
 * edit (`workItemListsChanged`) re-reads only the queries on screen, so the
 * record a closed side panel read keeps the old values, and every table cell
 * reading through `getIssueById` would show them over the re-read row (NG-3468).
 * Still the record when no newer row holds the item: stale beats nothing.
 */
export function findCachedWorkItem(
  client: QueryClient,
  tenant: string,
  workItemId: string,
): TIssue | undefined {
  let stale: { record: TIssue; readAt: number } | undefined;
  for (const query of detailRecords(client, tenant)) {
    const record = query.state.data as TIssue | undefined;
    if (record?.id !== workItemId) continue;
    if (!query.state.isInvalidated) return record;
    stale ??= { record, readAt: query.state.dataUpdatedAt };
  }
  let occurrence: TIssue | undefined;
  for (const query of rowLists(client, tenant)) {
    const data = query.state.data as ListData | undefined;
    if (!data) continue;
    if (stale && query.state.dataUpdatedAt <= stale.readAt) continue;
    for (const row of rowsOf(data)) {
      if (row.id !== workItemId) continue;
      if (!row.is_occurrence) return row;
      occurrence ??= row;
    }
  }
  return stale?.record ?? occurrence;
}

/**
 * Patch every cached row `match` picks with `patch`, in every list of `tenant`
 * and in every full record read. `patch` gets the row and returns its replacement.
 */
export function patchCachedWorkItems(
  client: QueryClient,
  tenant: string,
  match: (row: TIssue) => boolean,
  patch: (row: TIssue) => TIssue,
): void {
  for (const query of rowLists(client, tenant)) {
    rewrite(client, query, (data) => mapRows(data, (row) => (match(row) ? patch(row) : row)));
  }
  for (const query of detailRecords(client, tenant)) {
    const record = query.state.data as TIssue | undefined;
    if (record && match(record)) rewriteRecord(client, query, patch(record));
  }
}

/**
 * Merge `fields` into the work item's row in every list. A series' occurrence
 * rows carry their own dates, so by default only the item's own rows change,
 * as the store's by-id map did; `occurrences` patches those too (a field every
 * occurrence shows, such as the assignee).
 */
export function patchCachedWorkItem(
  client: QueryClient,
  tenant: string,
  workItemId: string,
  fields: Partial<TIssue>,
  { occurrences = false }: { occurrences?: boolean } = {},
): void {
  patchCachedWorkItems(
    client,
    tenant,
    (row) => row.id === workItemId && (occurrences || !row.is_occurrence),
    (row) => ({ ...row, ...fields }),
  );
}

/** Drop rows of these work items (occurrences included) from every list, counts following. */
export function removeCachedWorkItems(
  client: QueryClient,
  tenant: string,
  workItemIds: readonly string[],
): void {
  const ids = new Set(workItemIds);
  for (const query of rowLists(client, tenant)) {
    rewrite(client, query, (data) => mapRows(data, (row) => (ids.has(row.id) ? null : row)));
  }
}

/** A parent's cached children, under any principal. */
function subTaskLists(client: QueryClient, tenant: string, parentId?: string): Query[] {
  return client.getQueryCache().findAll({
    predicate: (query) =>
      query.queryKey[0] === tenant &&
      familyOf(query) === "sub-issues" &&
      (parentId === undefined || query.queryKey[4] === parentId),
  });
}

/**
 * Drop a work item from every parent's cached children, counts following, and
 * name the parents that held it. An archived child leaves its parent's list at
 * once: the server lists only active children.
 */
export function removeCachedSubTask(client: QueryClient, tenant: string, workItemId: string) {
  const parents: string[] = [];
  for (const query of subTaskLists(client, tenant)) {
    const data = query.state.data as ListData | undefined;
    if (!data || !rowsOf(data).some((row) => row.id === workItemId)) continue;
    parents.push(String(query.queryKey[4]));
    rewrite(client, query, (current) =>
      mapRows(current, (row) => (row.id === workItemId ? null : row)),
    );
  }
  return parents;
}

/** Add a created child to its parent's cached children at once; the re-read confirms it. */
export function appendCachedSubTask(
  client: QueryClient,
  tenant: string,
  parentId: string,
  child: TIssue,
): void {
  for (const query of subTaskLists(client, tenant, parentId)) {
    rewrite(client, query, (current) => {
      if (isPages(current) || current.rows.some((row) => row.id === child.id)) return current;
      return {
        ...current,
        rows: [...current.rows, child],
        totalCount: (current.totalCount ?? current.rows.length) + 1,
      };
    });
  }
}

/** Picks these parents' cached children (every parent's when none are named). */
export function subTaskListsOf(tenant: string, parentIds?: ReadonlySet<string>) {
  return (query: Query): boolean =>
    query.queryKey[0] === tenant &&
    familyOf(query) === "sub-issues" &&
    (parentIds === undefined || parentIds.has(String(query.queryKey[4])));
}

/** Re-read these parents' cached children (every parent's when none are named). */
export function rereadSubTasks(
  client: QueryClient,
  tenant: string,
  parentIds?: ReadonlySet<string>,
): Promise<void> {
  return rereadAfterWrite(client, subTaskListsOf(tenant, parentIds));
}

/** Every cached list holding one of `workItemIds`, with its rows of them, to put back. */
interface RowSnapshot {
  readonly entries: ReadonlyArray<readonly [readonly unknown[], ReadonlyMap<string, TIssue>]>;
  /** The full records read of them, by key. */
  readonly records: ReadonlyArray<readonly [readonly unknown[], TIssue]>;
}

function rowKey(row: TIssue): string {
  return row.is_occurrence && row.recurrence_id
    ? `${row.series_id ?? ""}:${row.recurrence_id}`
    : row.id;
}

function snapshotRows(client: QueryClient, tenant: string, ids: Set<string>): RowSnapshot {
  const entries: Array<readonly [readonly unknown[], ReadonlyMap<string, TIssue>]> = [];
  for (const query of rowLists(client, tenant)) {
    const data = query.state.data as ListData | undefined;
    if (!data) continue;
    const held = new Map<string, TIssue>();
    for (const row of rowsOf(data)) if (ids.has(row.id)) held.set(rowKey(row), row);
    if (held.size > 0) entries.push([query.queryKey, held]);
  }
  const records: Array<readonly [readonly unknown[], TIssue]> = [];
  for (const query of client.getQueryCache().findAll({ predicate: isDetailOf(tenant, ids) })) {
    const record = query.state.data as TIssue | undefined;
    if (record) records.push([query.queryKey, record]);
  }
  return { entries, records };
}

/** Put back the rows a failed write changed, leaving every other row as it is now. */
function restoreRows(client: QueryClient, snapshot: RowSnapshot): void {
  const cache = client.getQueryCache();
  for (const [key, held] of snapshot.entries) {
    const query = cache.find({ queryKey: key, exact: true });
    if (!query) continue;
    rewrite(client, query, (data) => mapRows(data, (row) => held.get(rowKey(row)) ?? row));
  }
  for (const [key, record] of snapshot.records) {
    const query = cache.find({ queryKey: key, exact: true });
    if (query) rewriteRecord(client, query, record);
  }
}

/**
 * Run a write to some work items against the cache (the Query side of the
 * store's `#2638` load tokens):
 *
 * 1. Cancel every list read in flight, and any read of the written items' full
 *    records: its server may read the rows before the write commits, and its
 *    answer would put the old rows back.
 * 2. `optimistic`, if given, patches the cache at once.
 * 3. On success the caller patches the server's answer in; on failure the
 *    written rows are put back in every list that held them, and the error
 *    rethrown. A full record read of a written item is put back with them.
 * 4. Then every list read while the write was in flight, or left with no data
 *    by the cancel, is re-read; the rest are marked stale, since a changed row
 *    may no longer belong to a filtered list or its count. `relist` re-reads
 *    every list instead (a create, delete, archive or restore, which changes
 *    which rows a list holds), or, as a predicate, the lists it picks (a move
 *    between parents, `subTaskListsOf`). The written items' full records are
 *    re-read or marked stale by the same rule.
 * 5. My Work's feed and availability, read under their own key, are re-read
 *    where shown and marked stale elsewhere (`myWorkChanged`, NG-3586).
 *
 * Re-read what a write changed through `relist`, not a re-read of your own
 * after it: step 4 picks its lists when it runs, so a list your re-read has
 * just refreshed counts as "read meanwhile" and is read twice.
 */
export async function writeWorkItemRows<T>(
  client: QueryClient,
  tenant: string,
  workItemIds: readonly string[],
  write: () => Promise<T>,
  {
    optimistic,
    relist = false,
  }: { optimistic?: () => void; relist?: boolean | ((query: Query) => boolean) } = {},
): Promise<T> {
  const isRecord = isDetailOf(tenant, new Set(workItemIds));
  const isTouched = (query: Query) => isAnyListQuery(tenant, query) || isRecord(query);
  const startedAt = Date.now();
  await client.cancelQueries({ predicate: isTouched });
  const snapshot = snapshotRows(client, tenant, new Set(workItemIds));
  try {
    optimistic?.();
    return await write();
  } catch (error) {
    restoreRows(client, snapshot);
    throw error;
  } finally {
    const reread = (query: Query) =>
      isTouched(query) &&
      (relist === true ||
        (typeof relist === "function" && relist(query)) ||
        query.state.data === undefined ||
        query.state.dataUpdatedAt >= startedAt);
    rereadAfterWrite(client, reread).catch((error: unknown) => {
      console.warn("[work-items] re-reading lists after a write failed", { tenant, error });
    });
    client
      .invalidateQueries({ predicate: (q) => isTouched(q) && !reread(q), refetchType: "none" })
      .catch((error: unknown) => {
        console.warn("[work-items] marking lists stale after a write failed", { tenant, error });
      });
    myWorkChanged(client, tenant);
  }
}

/**
 * Re-read every work-item list of `tenant` (a write whose effect on the lists is
 * unknown). Through `rereadAfterWrite`, like every post-write re-read here, so a
 * list still on its first read is read again rather than joined (NG-2135).
 * My Work's reads follow (`myWorkChanged`, NG-3586).
 */
export function invalidateWorkItemLists(client: QueryClient, tenant: string): Promise<void> {
  myWorkChanged(client, tenant);
  return rereadAfterWrite(client, (query) => isAnyListQuery(tenant, query));
}

/**
 * Tell the cache a write outside `IssueStore` changed work items (capacity
 * reassignments, the bulk endpoints, a sub-task create): every cached list and
 * full record of `tenant` is marked stale and the ones on screen re-read, so an
 * open side panel shows a bulk assignment at once rather than on the next
 * focus. My Work's reads follow (`myWorkChanged`, NG-3586). Before the app has
 * a query client nothing is cached, so this does nothing. Never rejects; a
 * failed re-read is the cache's to log.
 */
export function workItemListsChanged(tenant: string): void {
  const client = peekQueryClient();
  if (!client) return;
  myWorkChanged(client, tenant);
  client
    .invalidateQueries({
      predicate: (query) => isAnyListQuery(tenant, query) || isDetailQuery(tenant, query),
    })
    .catch((error: unknown) => {
      console.warn("[work-items] re-reading work items after a write failed", { tenant, error });
    });
}

/**
 * A work item moved between parents by a write outside `IssueStore` (the event
 * page's, NG-3470): both parents' children are re-read where a screen shows
 * them, and their records and every list are marked stale, so each parent's
 * progress and a table's expand arrow are right on their next read. The moved
 * item's own record is its writer's. `IssueStore`'s own move does the same
 * through `writeWorkItemRows`' `relist`. A missing end (a promote to top level,
 * NG-3429) also changes which rows the top-level lists hold, so the lists on
 * screen are re-read at once, as `IssueStore`'s promote does. Before the app has
 * a query client nothing is cached, so this does nothing. Never rejects.
 */
export function workItemReparented(
  tenant: string,
  parentIds: Iterable<string | null | undefined>,
): void {
  const client = peekQueryClient();
  const ends = [...parentIds];
  const ids = new Set(ends.filter((id): id is string => Boolean(id)));
  if (!client || ids.size === 0) return;
  const topLevelChanged = ends.some((id) => !id);
  const isParentRecord = isDetailOf(tenant, ids);
  const warn = (error: unknown) =>
    console.warn("[work-items] re-reading after a move between parents failed", { tenant, error });
  const touched = (query: Query) => isAnyListQuery(tenant, query) || isParentRecord(query);
  if (topLevelChanged) {
    // Every list on screen, the parents' children among them, read once.
    rereadAfterWrite(client, touched).catch(warn);
    return;
  }
  client.invalidateQueries({ predicate: touched, refetchType: "none" }).catch(warn);
  rereadSubTasks(client, tenant, ids).catch(warn);
}

/**
 * A top-level work item converted into a sub-item of `parentId` (NG-3472): it
 * and its sub-items moved under that parent, and an event's items under its
 * sessions moved up to it. Both parents are re-read as `workItemReparented`
 * does. The item takes `converted`, the server's answer, wherever it is cached;
 * with none (a queued convert, done in the background) its record is re-read
 * where a screen shows it. The server does not name the sub-items that moved, so
 * every other full record of `tenant` is marked stale too: one read earlier
 * would otherwise show its old parent. Never rejects.
 */
export function workItemConvertedToSubItem(
  tenant: string,
  itemId: string,
  parentId: string,
  converted: TIssue | null,
): void {
  workItemReparented(tenant, [parentId, itemId]);
  const client = peekQueryClient();
  if (!client) return;
  const warn = (error: unknown) =>
    console.warn("[work-items] re-reading records after a convert failed", { tenant, error });
  client
    .invalidateQueries({ predicate: (query) => isDetailQuery(tenant, query), refetchType: "none" })
    .catch(warn);
  if (converted) {
    patchCachedWorkItem(client, tenant, itemId, converted);
  } else {
    client.invalidateQueries({ predicate: isDetailOf(tenant, new Set([itemId])) }).catch(warn);
  }
}
